import { z } from 'zod';
import { commercialModelSchema, currencySchema, forecastConfidenceSchema, healthStatusSchema, projectWorkTypeSchema, } from './common.js';
import { projectContractStatusSchema } from './portfolio.js';
export const standardRateInputSchema = z
    .object({
    expertiseId: z.uuid(),
    billRateAmountMinor: z.number().int().positive().max(10_000_000),
    currency: z.enum(['CAD', 'USD']),
    effectiveFrom: z.iso.date(),
    effectiveTo: z.iso.date().nullable().default(null),
})
    .refine((value) => !value.effectiveTo || value.effectiveFrom <= value.effectiveTo, {
    message: 'The standard rate period is invalid.',
    path: ['effectiveTo'],
});
export const financialForecastSchema = z.object({
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    currency: z.literal('CAD'),
    months: z.array(z.object({
        month: z.string().regex(/^\d{4}-\d{2}$/),
        officialMinutes: z.number().int().nonnegative(),
        provisionalMinutes: z.number().int().nonnegative(),
        officialRevenueCadMinor: z.number().int().nonnegative(),
        provisionalRevenueCadMinor: z.number().int().nonnegative(),
    })),
    rows: z.array(z.object({
        month: z.string(),
        projectId: z.uuid(),
        projectName: z.string(),
        teamNames: z.array(z.string()),
        tempoTeamIds: z.array(z.string()).optional(),
        expertiseId: z.uuid().nullable(),
        expertiseName: z.string(),
        resourceId: z.uuid().nullable(),
        resourceName: z.string().nullable(),
        officialMinutes: z.number().int().nonnegative(),
        provisionalMinutes: z.number().int().nonnegative(),
        officialRevenueNativeMinor: z.number().int().nonnegative().nullable(),
        provisionalRevenueNativeMinor: z.number().int().nonnegative().nullable(),
        nativeCurrency: z.enum(['CAD', 'USD']),
        officialRevenueCadMinor: z.number().int().nonnegative().nullable(),
        provisionalRevenueCadMinor: z.number().int().nonnegative().nullable(),
        rateSource: z.enum(['project', 'standard']).nullable(),
    })),
    missingRates: z.array(z.object({
        projectId: z.uuid(),
        projectName: z.string(),
        expertiseId: z.uuid().nullable(),
        expertiseName: z.string(),
        month: z.string(),
        reason: z.string(),
    })),
});
export const projectForecastSettingsInputSchema = z.object({
    workType: projectWorkTypeSchema,
    commercialModel: commercialModelSchema.nullable(),
    remainingAdjustmentMinutes: z.number().int().min(-10_000_000).max(10_000_000),
    remainingAdjustmentReason: z.string().trim().max(2_000).nullable(),
    delayAtRiskBusinessDays: z.number().int().min(0).nullable(),
    delayCriticalBusinessDays: z.number().int().min(0).nullable(),
    budgetAtRiskBasisPoints: z.number().int().min(0).nullable(),
    budgetCriticalBasisPoints: z.number().int().min(0).nullable(),
});
export const rateImportRoleMappingInputSchema = z.object({
    sourceRole: z.string().trim().min(1).max(240),
    tempoRoleId: z.string().trim().min(1).max(128),
});
export const rateImportProjectCurrencyUpdateInputSchema = z.object({
    projectId: z.uuid(),
    currency: z.enum(['CAD', 'USD']),
});
export const canonicalRateImportInputSchema = z.object({
    sourceLabel: z.string().trim().min(1).max(240),
    csv: z.string().min(1).max(2_000_000),
    roleMappings: z.array(rateImportRoleMappingInputSchema).max(100).default([]),
    projectCurrencyUpdates: z.array(rateImportProjectCurrencyUpdateInputSchema).max(100).default([]),
});
export const rateImportIssueSchema = z.object({
    row: z.number().int().positive(),
    code: z.string().min(1).max(128),
    field: z.string().min(1).max(240).nullable(),
});
export const rateImportSummarySchema = z.object({
    sourceLabel: z.string().min(1).max(240),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/),
    acceptedCount: z.number().int().nonnegative(),
    rejectedCount: z.number().int().nonnegative(),
    issues: z.array(rateImportIssueSchema),
    confirmable: z.boolean(),
});
export const forecastScenarioSchema = z.object({
    name: z.enum(['optimistic', 'predicted', 'conservative']),
    completionDate: z.iso.date().nullable(),
});
export const projectInsightSchema = z.object({
    projectId: z.uuid(),
    asOfDate: z.iso.date(),
    contractStatus: projectContractStatusSchema,
    confidence: forecastConfidenceSchema,
    planCompletionDate: z.iso.date().nullable(),
    scenarios: z.array(forecastScenarioSchema),
    remainingMinutes: z.number().int(),
    futureAllocatedMinutes: z.number().int().nonnegative(),
    unallocatedShortfallMinutes: z.number().int().nonnegative(),
    taskPerformanceRatio: z.number().nullable(),
    teamFocusRatio: z.number().nullable(),
    taskPerformanceObservationCount: z.number().int().nonnegative(),
    teamFocusObservationCount: z.number().int().nonnegative(),
    billableUtilizationBasisPoints: z.number().int().nullable(),
    predictedDelayBusinessDays: z.number().int().nullable(),
    budgetVarianceBasisPoints: z.number().int().nullable(),
    thresholds: z.object({
        delayAtRiskBusinessDays: z.number().int().nonnegative(),
        delayCriticalBusinessDays: z.number().int().nonnegative(),
        budgetAtRiskBasisPoints: z.number().int().nonnegative(),
        budgetCriticalBasisPoints: z.number().int().nonnegative(),
        focusAtRiskBasisPoints: z.number().int().nonnegative(),
        focusCriticalBasisPoints: z.number().int().nonnegative(),
        billableAtRiskBasisPoints: z.number().int().nonnegative(),
        billableCriticalBasisPoints: z.number().int().nonnegative(),
        delaySource: z.enum(['organization', 'project']),
        budgetSource: z.enum(['organization', 'project']),
    }),
    health: z.object({
        delay: healthStatusSchema,
        budget: healthStatusSchema,
        focus: healthStatusSchema,
        billable: healthStatusSchema,
    }),
    financeEnabled: z.boolean(),
    financialsComplete: z.boolean(),
    financials: z.object({
        currency: currencySchema,
        commercialModel: commercialModelSchema.nullable(),
        budgetAmountMinor: z.number().int(),
        actualBillableValueMinor: z.number().int(),
        forecastBillableValueToCompleteMinor: z.number().int().nullable(),
        totalBillableValueAtCompletionMinor: z.number().int().nullable(),
    }),
    warnings: z.array(z.string()),
});
//# sourceMappingURL=forecast.js.map